/**
 * PATH: apps/ai/src/pages/dashboard/EmailDraftModal.tsx
 * PURPOSE: Outlook email draft creation modal
 */

import { useState } from 'react';
import { Send, Loader2, Mail, Check, AlertCircle, XCircle, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { createOutlookDraft, openDraftInOutlook } from '../../services/outlookService';

interface EmailDraftModalProps {
  onClose: () => void;
}

export function EmailDraftModal({ onClose }: EmailDraftModalProps) {
  const [emailDraft, setEmailDraft] = useState({ subject: 'Follow-up on Outstanding Invoice', body: '', to: '' });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailResult, setEmailResult] = useState<{ type: 'success' | 'error'; message: string; webLink?: string } | null>(null);

  const handleCreateEmailDraft = async () => {
    setEmailLoading(true);
    setEmailResult(null);
    try {
      const response = await createOutlookDraft({
        subject: emailDraft.subject,
        bodyHtml: emailDraft.body || '<p></p>',
        to: emailDraft.to.split(',').map(e => e.trim()).filter(Boolean),
      });
      setEmailResult({ type: 'success', message: 'Draft created successfully!', webLink: response.webLink });
    } catch (error: unknown) {
      setEmailResult({ type: 'error', message: (error as Error).message || 'Failed to create draft' });
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="relative w-full max-w-lg bg-card rounded-xl shadow-2xl overflow-hidden border border-border">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10"><Mail className="h-5 w-5 text-blue-500" /></div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Create Outlook Draft</h2>
              <p className="text-xs text-muted-foreground">Opens in your Outlook drafts</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors">
            <XCircle className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">To</label>
            <input type="text" value={emailDraft.to} onChange={(e) => setEmailDraft(prev => ({ ...prev, to: e.target.value }))}
              placeholder="recipient@example.com"
              className="w-full px-3 py-2 rounded-lg border border-border bg-muted/30 text-foreground text-sm focus:ring-2 focus:ring-primary focus:border-transparent" />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Subject</label>
            <input type="text" value={emailDraft.subject} onChange={(e) => setEmailDraft(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Email subject"
              className="w-full px-3 py-2 rounded-lg border border-border bg-muted/30 text-foreground text-sm focus:ring-2 focus:ring-primary focus:border-transparent" />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Message (optional)</label>
            <textarea value={emailDraft.body} onChange={(e) => setEmailDraft(prev => ({ ...prev, body: e.target.value }))}
              placeholder="Start typing your message..." rows={5}
              className="w-full px-3 py-2 rounded-lg border border-border bg-muted/30 text-foreground text-sm focus:ring-2 focus:ring-primary focus:border-transparent resize-none" />
          </div>
          {emailResult && (
            <div className={cn("flex items-center gap-3 p-3 rounded-lg", emailResult.type === 'success' ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600')}>
              {emailResult.type === 'success' ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
              <span className="flex-1 text-sm">{emailResult.message}</span>
              {emailResult.webLink && (
                <button onClick={() => openDraftInOutlook(emailResult.webLink!)}
                  className="flex items-center gap-1 px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white rounded-md text-xs transition-colors">
                  <ExternalLink className="h-3 w-3" /> Open
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border bg-muted/20">
          <button onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:bg-muted/50 rounded-lg transition-colors">Cancel</button>
          <button onClick={handleCreateEmailDraft} disabled={emailLoading || !emailDraft.subject.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg text-sm transition-colors">
            {emailLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Create Draft
          </button>
        </div>
      </div>
    </div>
  );
}
